import { useEffect, useState } from "react";
import { XCircle } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { extractApiError, type LocacaoDto } from "@/services/locacoes";
import { upperLocacaoText } from "./upperLocacaoText";

interface Props {
  open: boolean;
  locacao: LocacaoDto | null;
  busy?: boolean;
  onClose: () => void;
  onConfirm: (motivo: string) => Promise<void>;
  onMutationComplete?: () => void | Promise<void>;
}

export function ModalCancelarLocacao({ open, locacao, busy, onClose, onConfirm, onMutationComplete }: Props) {
  const [motivo, setMotivo] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setMotivo("");
      setError(null);
    }
  }, [open, locacao]);

  const handleSubmit = async () => {
    setError(null);
    try {
      await onConfirm(motivo.trim());
      await onMutationComplete?.();
      onClose();
    } catch (e: unknown) {
      setError(extractApiError(e, "Erro ao cancelar locação."));
    }
  };

  const title = (
    <div className="flex items-center gap-3">
      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-50">
        <XCircle className="h-4 w-4 text-red-600" />
      </div>
      <span className="font-bold text-slate-900">Cancelar locação</span>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Voltar
          </Button>
          <Button type="button" variant="danger" onClick={() => void handleSubmit()} loading={busy}>
            Confirmar cancelamento
          </Button>
        </div>
      }
    >
      {locacao && (
        <div className="space-y-4">
          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800">{error}</div>
          )}
          <p className="text-sm text-slate-600">
            A máquina <span className="font-semibold text-slate-800">{locacao.maquinaNome}</span> voltará a ficar
            disponível. Esta ação não pode ser desfeita.
          </p>
          {locacao.contratoLabel && (
            <p className="text-sm text-slate-500">
              Contrato: <span className="font-medium text-slate-700">{locacao.contratoLabel}</span>
            </p>
          )}
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Motivo (opcional)</label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(upperLocacaoText(e.target.value))}
              rows={3}
              placeholder="Ex.: locação aberta por engano, máquina retornou antes do previsto…"
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
            />
          </div>
        </div>
      )}
    </Modal>
  );
}
